export interface ColorOption {
  id: string
  name: string
  hex: string
  selected?: boolean
}

export interface InteriorOption {
  id: string
  name: string
  thumbnail?: string
  selected?: boolean
  description?: string
  colorOptions?: ColorOption[]
}

export interface InteriorMenuItem {
  id: string
  name: string
  type: 'category' | 'item' | 'option'
  icon?: string
  children?: InteriorMenuItem[]
  options?: InteriorOption[]
  expanded?: boolean
  selected?: boolean
  isPackage?: boolean
}

// Reusable placeholder thumbnails
const T = {
  a: 'https://cdn.magicpatterns.com/uploads/56xcQygN48ytC9vCWfbKsD/2a3e8f16-fd9a-4893-b4aa-68846e8eee8d.png',
  b: 'https://cdn.magicpatterns.com/uploads/sj8L76yNDFjCGFZWdgGY4N/9d9925e9-c3ea-4a0d-80fb-ffcfee4e866e.png',
  c: 'https://cdn.magicpatterns.com/uploads/4rsTQoSw8cboz4u88BHB6e/31666bba-be57-4389-96b2-845051abc30a.png',
  d: 'https://cdn.magicpatterns.com/uploads/8uMrohCuZZGCZe6mp1Ebh5/bdab08d5-af5f-4768-afba-d5b3c4e4d4f4.png',
  e: 'https://cdn.magicpatterns.com/uploads/rWjZv6Mm4vUpRox9hUrK2K/92c595c0-b3df-4f96-84f9-414aad4eea5b.png',
  f: 'https://cdn.magicpatterns.com/uploads/cZReokz7WQeKktQan8Tx5j/c4f38fb8-8ad6-4d2b-ba7a-5cae3db41a44.png',
  g: 'https://cdn.magicpatterns.com/uploads/mVj6c7zkUqrRquwCmxWcL6/15d95c32-8149-4132-9785-f1bfe49bb891.png',
  h: 'https://cdn.magicpatterns.com/uploads/kpH5AVoc585CmkhtFwT2gu/bb2e92e2-95c2-4917-a8f9-e317d073068e.png',
  i: 'https://cdn.magicpatterns.com/uploads/hA4ppMCeeEnfUr8DdemFhK/80b42e57-750f-436c-8070-ab27d0716845.png',
  j: 'https://cdn.magicpatterns.com/uploads/xefSXa2PcHLjXAgiprF9C2/94335722-d546-4e85-83cf-af86687637c9.png',
}

export const interiorMenuData: InteriorMenuItem[] = [
  {
    id: 'layouts',
    name: 'Layouts',
    type: 'category',
    children: [
      {
        id: 'plan-options',
        name: 'Plan Options',
        type: 'item',
      },
    ],
  },
  {
    id: 'main-options',
    name: 'Main Options',
    type: 'category',
    children: [
      {
        id: 'general',
        name: 'General',
        type: 'item',
      },
      {
        id: 'kitchen',
        name: 'Kitchen',
        type: 'item',
        children: [
          {
            id: 'kitchen-packages',
            name: 'Kitchen Packages',
            type: 'item',
            isPackage: true,
          },
          { id: 'kitchen-layout', name: 'Layout', type: 'item' },
          {
            id: 'refrigerator',
            name: 'Refrigerator',
            type: 'item',
            options: [
              {
                id: 'fridge-standard',
                name: 'Standard 30"',
                thumbnail: T.a,
                selected: true,
                colorOptions: [
                  {
                    id: 'fs-stainless',
                    name: 'Stainless',
                    hex: '#C0C0C0',
                    selected: true,
                  },
                  { id: 'fs-white', name: 'White', hex: '#F5F5F5' },
                  { id: 'fs-black', name: 'Black', hex: '#2D2D2D' },
                ],
              },
              {
                id: 'fridge-french-door',
                name: 'French Door 36"',
                thumbnail: T.b,
                colorOptions: [
                  {
                    id: 'ffd-stainless',
                    name: 'Stainless',
                    hex: '#C0C0C0',
                    selected: true,
                  },
                  {
                    id: 'ffd-black-ss',
                    name: 'Black Stainless',
                    hex: '#3A3A3A',
                  },
                  { id: 'ffd-white', name: 'White', hex: '#F5F5F5' },
                  { id: 'ffd-slate', name: 'Slate', hex: '#708090' },
                ],
              },
              {
                id: 'fridge-side-by-side',
                name: 'Side-by-Side',
                thumbnail: T.c,
                colorOptions: [
                  {
                    id: 'fsbs-stainless',
                    name: 'Stainless',
                    hex: '#C0C0C0',
                    selected: true,
                  },
                  { id: 'fsbs-black', name: 'Black', hex: '#2D2D2D' },
                ],
              },
              {
                id: 'fridge-counter-depth',
                name: 'Counter Depth',
                thumbnail: T.d,
                colorOptions: [
                  {
                    id: 'fcd-stainless',
                    name: 'Stainless',
                    hex: '#C0C0C0',
                    selected: true,
                  },
                  {
                    id: 'fcd-black-ss',
                    name: 'Black Stainless',
                    hex: '#3A3A3A',
                  },
                  { id: 'fcd-white', name: 'White', hex: '#F5F5F5' },
                  {
                    id: 'fcd-fingerprint',
                    name: 'Fingerprint Resistant',
                    hex: '#B8B8B8',
                  },
                ],
              },
              {
                id: 'fridge-smart',
                name: 'Smart Hub',
                thumbnail: T.e,
                colorOptions: [
                  {
                    id: 'fsh-stainless',
                    name: 'Stainless',
                    hex: '#C0C0C0',
                    selected: true,
                  },
                  {
                    id: 'fsh-black-ss',
                    name: 'Black Stainless',
                    hex: '#3A3A3A',
                  },
                  { id: 'fsh-white', name: 'White', hex: '#F5F5F5' },
                  { id: 'fsh-navy', name: 'Family Hub Navy', hex: '#1B3A5C' },
                ],
              },
              {
                id: 'fridge-panel-ready',
                name: 'Panel Ready',
                thumbnail: T.f,
                colorOptions: [
                  {
                    id: 'fpr-panel',
                    name: 'Custom Panel',
                    hex: '#D4C5A9',
                    selected: true,
                  },
                ],
              },
            ],
          },
          { id: 'cooktop', name: 'Cooktop', type: 'item' },
          {
            id: 'range-oven',
            name: 'Range Oven',
            type: 'item',
            options: [
              {
                id: 'standard',
                name: 'Standard',
                thumbnail:
                  'https://cdn.magicpatterns.com/uploads/ipGLrgJ5dnUHzeX467UrRX/99451023-beef-4830-b2e4-6b5c5bbc05fa.png',
                selected: true,
                colorOptions: [
                  {
                    id: 'ro-std-white',
                    name: 'White',
                    hex: '#F5F5F5',
                    selected: true,
                  },
                  { id: 'ro-std-black', name: 'Black', hex: '#2D2D2D' },
                ],
              },
              {
                id: 'level-1',
                name: 'Stainless Steel',
                thumbnail:
                  'https://cdn.magicpatterns.com/uploads/7qRjsZBrCQMtsgwG6dR6aa/a12724ab-875b-49f0-82d7-cbbbc5f8d329.png',
                colorOptions: [
                  {
                    id: 'ro-l1-stainless',
                    name: 'Stainless',
                    hex: '#C0C0C0',
                    selected: true,
                  },
                ],
              },
              {
                id: 'level-2',
                name: 'Professional',
                thumbnail:
                  'https://cdn.magicpatterns.com/uploads/rBmiA4HJkMsYLa3DUGGUeP/a38be756-541f-4f67-b58f-3e7cff68d49c.png',
                colorOptions: [
                  {
                    id: 'ro-l2-stainless',
                    name: 'Stainless',
                    hex: '#C0C0C0',
                    selected: true,
                  },
                  { id: 'ro-l2-matte', name: 'Matte Black', hex: '#1A1A1A' },
                ],
              },
              {
                id: 'level-3',
                name: 'Chef Series',
                thumbnail:
                  'https://cdn.magicpatterns.com/uploads/i3anGUuRyR4DYNoRawPTEF/ba5cadb3-2c6f-4c61-b770-e245581bd9ca.png',
                colorOptions: [
                  {
                    id: 'ro-l3-stainless',
                    name: 'Stainless',
                    hex: '#C0C0C0',
                    selected: true,
                  },
                  { id: 'ro-l3-matte', name: 'Matte Black', hex: '#1A1A1A' },
                  { id: 'ro-l3-burgundy', name: 'Burgundy', hex: '#722F37' },
                ],
              },
              {
                id: 'level-4',
                name: 'Commercial Grade',
                thumbnail:
                  'https://cdn.magicpatterns.com/uploads/j59ZjLMVTNqtgapFimQc1E/08ea5d1f-e4b6-4d72-8184-56c394b9cb43.png',
                colorOptions: [
                  {
                    id: 'ro-l4-stainless',
                    name: 'Stainless',
                    hex: '#C0C0C0',
                    selected: true,
                  },
                ],
              },
              {
                id: 'black-electric',
                name: 'Black Stainless',
                thumbnail:
                  'https://cdn.magicpatterns.com/uploads/rCCBzCc61oxdxptZB8CK3X/7e166a12-77dc-4c11-b902-19ee335133af.png',
                colorOptions: [
                  {
                    id: 'ro-be-blackss',
                    name: 'Black Stainless',
                    hex: '#3A3A3A',
                    selected: true,
                  },
                ],
              },
              {
                id: 'black-gas',
                name: 'Matte Black',
                thumbnail:
                  'https://cdn.magicpatterns.com/uploads/6x4JkKCohmWMYizkdUVv5H/c5ee6763-a055-4b4c-9623-560075c646ed.png',
                colorOptions: [
                  {
                    id: 'ro-bg-matte',
                    name: 'Matte Black',
                    hex: '#1A1A1A',
                    selected: true,
                  },
                ],
              },
            ],
          },
          {
            id: 'dishwasher',
            name: 'Dishwasher',
            type: 'item',
            options: [
              {
                id: 'dw-standard',
                name: 'Standard',
                thumbnail:
                  'https://cdn.magicpatterns.com/uploads/8WnWtNRoYDXghwDDYKxMY1/3a7bf4b8-f2ce-43ab-812d-d2135155715f.png',
                selected: true,
              },
              {
                id: 'dw-quiet',
                name: 'Ultra Quiet',
                thumbnail:
                  'https://cdn.magicpatterns.com/uploads/9HgKuy26y4nFyrfLCdGjh9/43af1ca4-cf13-471b-bebe-8bd2e03fcbfb.png',
              },
              {
                id: 'dw-third-rack',
                name: 'Third Rack',
                thumbnail:
                  'https://cdn.magicpatterns.com/uploads/trdsaseMYftX5ReXeRVWmo/728deced-e8d7-48dc-a7f2-8c641afae38b.png',
              },
              {
                id: 'dw-panel-ready',
                name: 'Panel Ready',
                thumbnail:
                  'https://cdn.magicpatterns.com/uploads/gXch4QkMwxNQ9Gf4G5EAdJ/b394dd48-02c4-4c34-a060-d76f1604fa7c.png',
              },
              {
                id: 'dw-smart',
                name: 'Smart Connect',
                thumbnail:
                  'https://cdn.magicpatterns.com/uploads/iQkCQnTBXVuPRJZw5CWi23/a3b5acff-a1c0-4442-84b1-286862e88499.png',
              },
            ],
          },
          { id: 'wall-oven', name: 'Wall Oven', type: 'item' },
          {
            id: 'microwave',
            name: 'Microwave',
            type: 'item',
            options: [
              {
                id: 'mw-standard',
                name: 'Standard',
                thumbnail:
                  'https://cdn.magicpatterns.com/uploads/7J9vR4HKQ2tpt65jg7G16W/74bb9c06-756a-4000-8501-7b776559d1c5.png',
                selected: true,
              },
              {
                id: 'mw-over-range',
                name: 'Over-the-Range',
                thumbnail:
                  'https://cdn.magicpatterns.com/uploads/raVYgQuqvg9FTcFXiVoWdF/d1779e5a-0f26-4813-b30b-c76d034a46e4.png',
              },
              {
                id: 'mw-drawer',
                name: 'Drawer Style',
                thumbnail:
                  'https://cdn.magicpatterns.com/uploads/9g5j9FPCgRFZUjU4MVxTD6/dcf7809d-faed-42b2-844a-3dab62702865.png',
              },
              {
                id: 'mw-built-in',
                name: 'Built-In',
                thumbnail:
                  'https://cdn.magicpatterns.com/uploads/4o8ktMaBmPjywuSkMVi4Um/e2e961b3-6f11-40d0-b1be-f04ad41d6c36.png',
              },
            ],
          },
          { id: 'hood-fan', name: 'Hood Fan', type: 'item' },
          {
            id: 'cabinet-profile',
            name: 'Cabinet Profile',
            type: 'item',
            options: [
              {
                id: 'cp-shaker',
                name: 'Shaker',
                thumbnail: T.g,
                selected: true,
              },
              { id: 'cp-flat', name: 'Flat Panel', thumbnail: T.h },
              { id: 'cp-raised', name: 'Raised Panel', thumbnail: T.i },
              { id: 'cp-beadboard', name: 'Beadboard', thumbnail: T.j },
            ],
            children: [
              {
                id: 'handle-model',
                name: 'Handle Model',
                type: 'item',
                options: [
                  {
                    id: 'hm-bar',
                    name: 'Bar Pull',
                    thumbnail: T.a,
                    selected: true,
                  },
                  { id: 'hm-knob', name: 'Knob', thumbnail: T.b },
                  { id: 'hm-cup', name: 'Cup Pull', thumbnail: T.c },
                  { id: 'hm-edge', name: 'Edge Pull', thumbnail: T.d },
                  { id: 'hm-ring', name: 'Ring Pull', thumbnail: T.e },
                ],
              },
              {
                id: 'handle-material',
                name: 'Handle Material',
                type: 'item',
                options: [
                  {
                    id: 'hmat-nickel',
                    name: 'Brushed Nickel',
                    thumbnail: T.g,
                    selected: true,
                  },
                  { id: 'hmat-black', name: 'Matte Black', thumbnail: T.h },
                  {
                    id: 'hmat-chrome',
                    name: 'Polished Chrome',
                    thumbnail: T.i,
                  },
                  {
                    id: 'hmat-brass',
                    name: 'Antique Brass',
                    thumbnail: T.j,
                  },
                  {
                    id: 'hmat-bronze',
                    name: 'Oil-Rubbed Bronze',
                    thumbnail: T.f,
                  },
                ],
              },
            ],
          },
          {
            id: 'upper-cabinet-finish',
            name: 'Upper Cabinet Finish',
            type: 'item',
          },
          {
            id: 'lower-cabinet-finish',
            name: 'Lower Cabinet Finish',
            type: 'item',
          },
          {
            id: 'countertop',
            name: 'Countertop',
            type: 'item',
            options: [
              {
                id: 'ct-laminate',
                name: 'Laminate',
                thumbnail: T.g,
                selected: true,
              },
              { id: 'ct-granite', name: 'Granite', thumbnail: T.h },
              { id: 'ct-quartz', name: 'Quartz', thumbnail: T.i },
              { id: 'ct-marble', name: 'Marble', thumbnail: T.j },
              {
                id: 'ct-butcher-block',
                name: 'Butcher Block',
                thumbnail:
                  'https://cdn.magicpatterns.com/uploads/f1hzbo3o3QLATPgnhLZGui/c9b16336-b6de-4ae1-8665-58f652453ea2.png',
              },
            ],
          },
          { id: 'backsplash', name: 'Backsplash', type: 'item' },
          {
            id: 'backsplash',
            name: 'Backsplash',
            type: 'item',
            options: [
              {
                id: 'bs-subway-white',
                name: 'White Subway Tile',
                thumbnail: T.g,
                selected: true,
              },
              {
                id: 'bs-subway-gray',
                name: 'Gray Subway Tile',
                thumbnail: T.h,
              },
              {
                id: 'bs-herringbone',
                name: 'Herringbone Mosaic',
                thumbnail: T.i,
              },
              { id: 'bs-hexagon', name: 'Hexagon Tile', thumbnail: T.j },
              { id: 'bs-marble-slab', name: 'Marble Slab', thumbnail: T.a },
              { id: 'bs-glass-mosaic', name: 'Glass Mosaic', thumbnail: T.b },
              { id: 'bs-zellige', name: 'Zellige Tile', thumbnail: T.c },
              { id: 'bs-penny-round', name: 'Penny Round', thumbnail: T.d },
              { id: 'bs-natural-stone', name: 'Natural Stone', thumbnail: T.e },
            ],
          },
          {
            id: 'flooring',
            name: 'Flooring',
            type: 'item',
            options: [
              {
                id: 'fl-hardwood-oak',
                name: 'White Oak Hardwood',
                thumbnail: T.g,
                selected: true,
              },
              {
                id: 'fl-hardwood-walnut',
                name: 'Walnut Hardwood',
                thumbnail: T.h,
              },
              {
                id: 'fl-engineered-hickory',
                name: 'Engineered Hickory',
                thumbnail: T.i,
              },
              {
                id: 'fl-lvp-standard',
                name: 'Luxury Vinyl Plank',
                thumbnail: T.j,
              },
              {
                id: 'fl-porcelain-tile',
                name: 'Porcelain Tile',
                thumbnail: T.a,
              },
              { id: 'fl-ceramic-tile', name: 'Ceramic Tile', thumbnail: T.b },
              { id: 'fl-natural-stone', name: 'Natural Stone', thumbnail: T.c },
              {
                id: 'fl-herringbone',
                name: 'Herringbone Parquet',
                thumbnail: T.d,
              },
            ],
          },
          { id: 'faucet', name: 'Faucet', type: 'item' },
          { id: 'faucet-finish', name: 'Faucet Finish', type: 'item' },
          { id: 'sink', name: 'Sink', type: 'item' },
          { id: 'sink-finish', name: 'Sink Finish', type: 'item' },
          { id: 'pot-filler', name: 'Pot Filler', type: 'item' },
          {
            id: 'railing',
            name: 'Railing',
            type: 'item',
            options: [
              {
                id: 'rail-traditional',
                name: 'Traditional',
                thumbnail: T.a,
                selected: true,
              },
              { id: 'rail-modern', name: 'Modern', thumbnail: T.b },
              { id: 'rail-craftsman', name: 'Craftsman', thumbnail: T.c },
              { id: 'rail-glass-panel', name: 'Glass Panel', thumbnail: T.d },
              { id: 'rail-cable', name: 'Cable Rail', thumbnail: T.e },
            ],
            children: [
              {
                id: 'railing-post',
                name: 'Post',
                type: 'item',
                options: [
                  {
                    id: 'post-square',
                    name: 'Square Post',
                    thumbnail: T.a,
                    selected: true,
                  },
                  { id: 'post-turned', name: 'Turned Post', thumbnail: T.b },
                  { id: 'post-craftsman', name: 'Craftsman', thumbnail: T.c },
                ],
              },
              {
                id: 'railing-cap',
                name: 'Post Cap',
                type: 'item',
                options: [
                  {
                    id: 'cap-flat',
                    name: 'Flat Cap',
                    thumbnail: T.d,
                    selected: true,
                  },
                  { id: 'cap-ball', name: 'Ball Cap', thumbnail: T.e },
                  { id: 'cap-acorn', name: 'Acorn Cap', thumbnail: T.f },
                ],
              },
              {
                id: 'railing-handrail',
                name: 'Handrail',
                type: 'item',
                options: [
                  {
                    id: 'hr-round',
                    name: 'Round Profile',
                    thumbnail: T.g,
                    selected: true,
                  },
                  { id: 'hr-flat', name: 'Flat Profile', thumbnail: T.h },
                  { id: 'hr-craftsman', name: 'Craftsman', thumbnail: T.i },
                ],
              },
              {
                id: 'railing-baluster',
                name: 'Baluster',
                type: 'item',
                options: [
                  {
                    id: 'bal-plain',
                    name: 'Plain Square',
                    thumbnail: T.a,
                    selected: true,
                  },
                  { id: 'bal-twisted', name: 'Twisted Iron', thumbnail: T.b },
                  { id: 'bal-basket', name: 'Basket Weave', thumbnail: T.c },
                  { id: 'bal-wood', name: 'Wood Turned', thumbnail: T.d },
                ],
              },
              {
                id: 'railing-material',
                name: 'Material',
                type: 'item',
                options: [
                  {
                    id: 'rail-mat-oak',
                    name: 'White Oak',
                    thumbnail: T.g,
                    selected: true,
                  },
                  { id: 'rail-mat-maple', name: 'Maple', thumbnail: T.h },
                  { id: 'rail-mat-iron', name: 'Wrought Iron', thumbnail: T.i },
                  {
                    id: 'rail-mat-painted',
                    name: 'Painted White',
                    thumbnail: T.j,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        id: 'bathroom',
        name: 'Bathroom',
        type: 'item',
      },
      {
        id: 'laundry',
        name: 'Laundry',
        type: 'item',
      },
      {
        id: 'refrigerators',
        name: 'Refrigerators',
        type: 'item',
        options: [
          {
            id: 'fridges-basic-28',
            name: 'Basic 28"',
            thumbnail: T.a,
            selected: true,
          },
          { id: 'fridges-standard-30', name: 'Standard 30"', thumbnail: T.b },
          { id: 'fridges-economy-24', name: 'Economy 24"', thumbnail: T.c },
          { id: 'fridges-stainless-30', name: 'Stainless 30"', thumbnail: T.d },
          { id: 'fridges-energy-star', name: 'Energy Star', thumbnail: T.e },
          { id: 'fridges-top-freezer', name: 'Top Freezer', thumbnail: T.f },
          {
            id: 'fridges-bottom-freezer',
            name: 'Bottom Freezer',
            thumbnail: T.a,
          },
          { id: 'fridges-compact-plus', name: 'Compact Plus', thumbnail: T.b },
          { id: 'fridges-french-33', name: 'French Door 33"', thumbnail: T.c },
          {
            id: 'fridges-side-by-side-36',
            name: 'Side-by-Side 36"',
            thumbnail: T.d,
          },
          {
            id: 'fridges-counter-33',
            name: 'Counter Depth 33"',
            thumbnail: T.e,
          },
          { id: 'fridges-glass-door', name: 'Glass Door', thumbnail: T.f },
          { id: 'fridges-column', name: 'Column Style', thumbnail: T.a },
          { id: 'fridges-smart-pro', name: 'Smart Hub Pro', thumbnail: T.e },
          { id: 'fridges-panel-36', name: 'Panel Ready 36"', thumbnail: T.f },
          { id: 'fridges-builtin-42', name: 'Built-In 42"', thumbnail: T.b },
          { id: 'fridges-pro-48', name: 'Professional 48"', thumbnail: T.c },
          { id: 'fridges-chef', name: 'Chef Series', thumbnail: T.d },
          { id: 'fridges-subzero-48', name: 'Sub-Zero 48"', thumbnail: T.f },
          {
            id: 'fridges-thermador',
            name: 'Thermador Freedom',
            thumbnail: T.a,
          },
          { id: 'fridges-viking', name: 'Viking Professional', thumbnail: T.b },
          { id: 'fridges-lacornue', name: 'La Cornue', thumbnail: T.c },
        ],
      },
      {
        id: 'refrigerators-lots',
        name: 'Refrigerators Lots',
        type: 'item',
        options: (() => {
          const baseOptions = [
            {
              id: 'fridges-basic-28',
              name: 'Basic 28"',
              thumbnail: T.a,
              selected: true,
            },
            { id: 'fridges-standard-30', name: 'Standard 30"', thumbnail: T.b },
            { id: 'fridges-economy-24', name: 'Economy 24"', thumbnail: T.c },
            {
              id: 'fridges-stainless-30',
              name: 'Stainless 30"',
              thumbnail: T.d,
            },
            { id: 'fridges-energy-star', name: 'Energy Star', thumbnail: T.e },
            { id: 'fridges-top-freezer', name: 'Top Freezer', thumbnail: T.f },
            {
              id: 'fridges-bottom-freezer',
              name: 'Bottom Freezer',
              thumbnail: T.a,
            },
            {
              id: 'fridges-compact-plus',
              name: 'Compact Plus',
              thumbnail: T.b,
            },
            {
              id: 'fridges-french-33',
              name: 'French Door 33"',
              thumbnail: T.c,
            },
            {
              id: 'fridges-side-by-side-36',
              name: 'Side-by-Side 36"',
              thumbnail: T.d,
            },
            {
              id: 'fridges-counter-33',
              name: 'Counter Depth 33"',
              thumbnail: T.e,
            },
            { id: 'fridges-glass-door', name: 'Glass Door', thumbnail: T.f },
            { id: 'fridges-column', name: 'Column Style', thumbnail: T.a },
            { id: 'fridges-smart-pro', name: 'Smart Hub Pro', thumbnail: T.e },
            { id: 'fridges-panel-36', name: 'Panel Ready 36"', thumbnail: T.f },
            { id: 'fridges-builtin-42', name: 'Built-In 42"', thumbnail: T.b },
            { id: 'fridges-pro-48', name: 'Professional 48"', thumbnail: T.c },
            { id: 'fridges-chef', name: 'Chef Series', thumbnail: T.d },
            { id: 'fridges-subzero-48', name: 'Sub-Zero 48"', thumbnail: T.f },
            {
              id: 'fridges-thermador',
              name: 'Thermador Freedom',
              thumbnail: T.a,
            },
            {
              id: 'fridges-viking',
              name: 'Viking Professional',
              thumbnail: T.b,
            },
            { id: 'fridges-lacornue', name: 'La Cornue', thumbnail: T.c },
          ]
          const result: typeof baseOptions = []
          for (let copy = 0; copy < 5; copy++) {
            for (const opt of baseOptions) {
              if (result.length >= 100) break
              result.push({
                ...opt,
                id:
                  copy === 0 ? `lots-${opt.id}` : `lots-${opt.id}-${copy + 1}`,
                selected: copy === 0 && opt.selected ? true : undefined,
              })
            }
            if (result.length >= 100) break
          }
          return result
        })(),
      },
    ],
  },
  {
    id: 'basement-options',
    name: 'Basement Options',
    type: 'category',
    children: [
      { id: 'basement-general', name: 'General', type: 'item' },
      { id: 'basement-kitchen', name: 'Kitchen', type: 'item' },
      { id: 'basement-bathroom', name: 'Bathroom', type: 'item' },
      { id: 'basement-laundry', name: 'Laundry', type: 'item' },
    ],
  },
]

export const exteriorMenuData: InteriorMenuItem[] = [
  {
    id: 'ext-main-options',
    name: 'Main Options',
    type: 'category',
    children: [
      {
        id: 'elevations',
        name: 'Elevations',
        type: 'item',
        children: [
          {
            id: 'ext-options',
            name: 'Options',
            type: 'item',
          },
        ],
      },
      {
        id: 'inspirations',
        name: 'Inspirations',
        type: 'item',
        children: [
          {
            id: 'ext-palettes',
            name: 'Palettes',
            type: 'item',
          },
        ],
      },
    ],
  },
]
